/** CrmRosterPicker — the eligible-people checklist for ROSTER-ONLY ref columns (PR3 item 7).
 *
 * `deal_team` / `deal_lead` are Ember-only by owner ruling, and the backend has enforced that since
 * CUT-15(d) (`lists.ref_write_refusal`). Only the EDITOR was wrong: they got the general directory
 * typeahead, so an operator could type any counterparty, pick them, and collect a server refusal. This
 * offers exactly what the write accepts — the active roster, as a THEMES-style checklist.
 *
 * Modeled on `CrmChecklistEditor` (same search + checkbox rows + chip strip), but committing entity
 * REFS rather than strings: `[{local_id,name}]` for a multi column, a single `{local_id,name}` dict or
 * `null` for a single one — the shape `_coerce_ref_value` expects (the name is display-only; the server
 * re-resolves it from `local_id`). `deal_lead` is radio-like: picking one REPLACES and closes.
 *
 * Which columns get this editor is decided by the column's `ref_options_source` flag, never by field
 * key — see `crmEditability.ROSTER_REF_SOURCE`.
 *
 * ⚠ **A selected person who is NOT in the picklist still renders (checked, as an "extra").** The
 * Affinity import is exempt from the roster rule by design, so imported deals really do carry externals
 * and departed staff on `deal_team`. Hiding them would (a) make them invisible to the one operator who
 * could clear them and (b) leave the cell un-committable for a reason the UI never showed — the write
 * gate refuses the WHOLE cell, so the fix has to be discoverable right here.
 *
 * ⚠ Refs are never dropped silently: the strip and the extras render from the cell's own ref objects,
 * removed by POSITION, so a ref whose id the FE cannot read (`refLocalId` → null) is still visible and
 * still clearable rather than quietly vanishing from the committed value.
 */
import { useMemo, useRef, useState } from 'react';
import type { CustomCellEditorProps } from 'ag-grid-react';
import styles from './crm.module.css';
import type { CrmRefValue, CrmRosterOption } from '../../types';
import { isRef } from './crmCells';
import { refLabel, refLocalId } from './EntityRefs';
import { cancelEditor, EditorCloseButton } from './crmEditorCancel';

const _normalizeRefs = (v: unknown): CrmRefValue[] => (Array.isArray(v) ? v : [v]).filter(isRef);
/** Roster columns are person columns, so one resolver for both stored shapes ({local_id} native, and
 *  {local_person_id} as other surfaces write it). */
const _idOf = (r: CrmRefValue): number | null => refLocalId(r, 'person');

export function CrmRosterPicker(
  props: CustomCellEditorProps & {
    people?: CrmRosterOption[]; single?: boolean; onCancelEdit?: () => void },
) {
  const { value, onValueChange, stopEditing } = props;
  const people = props.people ?? [];
  const single = !!props.single;
  // The value the editor OPENED with — what ✕ restores. A ref (not state) because it must not change as
  // the operator toggles, and AG-Grid's own `initialValue` prop does not exist in the panel host.
  const initial = useRef(value);
  const [refs, setRefs] = useState<CrmRefValue[]>(() => _normalizeRefs(value));
  const [q, setQ] = useState('');

  const commit = (next: CrmRefValue[]) => {
    setRefs(next);
    onValueChange(single ? (next[0] ?? null) : next);
  };
  const removeAt = (i: number) => commit(refs.filter((_, j) => j !== i));
  const pick = (p: CrmRosterOption) => {
    const at = refs.findIndex((r) => _idOf(r) === p.local_person_id);
    if (single) {
      // radio-like: a pick REPLACES the cell and closes. Clearing goes through the chip's × — the same
      // affordance RefCellEditor's single mode uses, and the reason the chip strip renders here too.
      commit([{ local_id: p.local_person_id, name: p.name }]);
      stopEditing();
      return;
    }
    if (at >= 0) removeAt(at);
    else commit([...refs, { local_id: p.local_person_id, name: p.name }]);
  };

  // Does the STAGED value still contain anyone off the roster? The write gate refuses the WHOLE cell
  // for one of these ("1 of 3 people are not on the active Ember team roster"), so on an imported deal
  // almost any edit fails until they are cleared. Stated up front rather than discovered on Save.
  const hasExternal = refs.some((r) => {
    const id = _idOf(r);
    return id === null || !people.some((p) => p.local_person_id === id);
  });

  const shown = useMemo(() => {
    const eligible = people.map((p) => ({ key: `r${p.local_person_id}`, name: p.name, option: p, at: -1 }));
    const known = new Set(people.map((p) => p.local_person_id));
    // selected-but-not-eligible (imported externals / departed staff) — checked, removable, LABELLED
    const extras = refs
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => { const id = _idOf(r); return id === null || !known.has(id); })
      .map(({ r, i }) => ({ key: `x${i}`, name: refLabel(r) ?? 'Restricted', option: null, at: i }));
    const all = [...eligible, ...extras];
    const n = q.trim().toLowerCase();
    return n ? all.filter((p) => p.name.toLowerCase().includes(n)) : all;
  }, [people, refs, q]);

  return (
    <div className={styles.refEditor}>
      <div className={styles.refEditorChips}>
        {refs.length === 0 && <span className={styles.gridEmpty}>none</span>}
        {refs.map((r, i) => {
          const label = refLabel(r) ?? 'Restricted';
          return (
            <span key={`${_idOf(r) ?? 'x'}-${i}`} className={styles.gridRef}>
              {label}
              <button className={styles.refEditorRemove} aria-label={`Remove ${label}`}
                      onClick={() => removeAt(i)}>×</button>
            </span>
          );
        })}
      </div>
      <input className={styles.refEditorSearch} autoFocus value={q} placeholder="Search the Ember team…"
             onChange={(e) => setQ(e.target.value)} />
      <div className={styles.refEditorResults}>
        {shown.length === 0 && <span className={styles.gridEmpty}>No teammates match.</span>}
        {shown.map((row) => (
          <label key={row.key} className={styles.checklistRow}>
            <input type={single ? 'radio' : 'checkbox'}
                   checked={row.option === null || refs.some((r) => _idOf(r) === row.option!.local_person_id)}
                   onChange={() => (row.option ? pick(row.option) : removeAt(row.at))} />
            {row.name}
            {row.option === null && <span className={styles.refEditorDetail}>not on the Ember team</span>}
          </label>
        ))}
      </div>
      {hasExternal && (
        <div className={styles.hint}>
          Deals can only be saved with Ember team members — remove the entries marked
          “not on the Ember team” first.
        </div>
      )}
      <div className={styles.refEditorFooter}>
        <EditorCloseButton onClick={() => cancelEditor(props, initial.current)} />
        {/* Done is UNCONDITIONAL, unlike CrmChecklistEditor's — copying `{!single && …}` from there was
            a real regression (adversarial review). Single mode auto-closes on a PICK, but a chip-×
            clear commits null and stays open, so in the panel host (which writes only on stopEditing)
            hiding Done left the operator no way to save the clear. `RefCellEditor`, the editor this
            replaces, always renders it. */}
        <button className={styles.gridPageBtn} onClick={() => stopEditing()}>Done</button>
      </div>
    </div>
  );
}
